import { errorText, get, post } from '@/api/client';
import { T } from '@/i18n';
import { KontoBox } from '@/pages/konto/shared';
import { useTenant } from '@/shell/tenant';
import { Button, LoadError, Skeleton, toast } from '@/ui';
import { fmtD } from '@/ui/format';
/**
 * /abonnement (plan 0028 slice 4, owner 2026-09-27): the price the server computes, the trial's end,
 * and the two ways to Stripe: Checkout to put in a card and start, the portal to change it, see
 * invoices or cancel. Coming back from Checkout proves nothing; the page asks the server until the
 * webhook has written the subscription. On the account screens' layout, outside the shell, like the
 * setup (owner, 2026-09-27).
 */
import { Notice } from '@/ui/page';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

export interface Billing {
  enabled: boolean;
  status: string | null;
  has_customer: boolean;
  trial_ends_at: string | null;
  paid_until: string | null;
  access: 'full' | 'read' | 'locked';
  grace_ends_at: string | null;
  quote: {
    seats: number;
    included: number;
    base_kr: number;
    seat_kr: number;
    monthly_ex_vat: number;
    mva_percent: number;
  };
}

const LIVE = ['trialing', 'active', 'past_due'];
const STATUS: Record<string, string> = {
  trialing: 'Aktivt, i prøveperioden',
  active: 'Aktivt',
  past_due: 'Betalingen feilet',
  canceled: 'Avsluttet',
  unpaid: 'Ubetalt',
  incomplete: 'Ikke fullført',
  incomplete_expired: 'Ikke fullført',
};
const day = (iso: string | null) =>
  iso ? fmtD(new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Oslo' }).format(new Date(iso))) : '';
const kr = (n: number) => `${n.toLocaleString('nb-NO')} kr`;

export default function Abonnement() {
  const [params] = useSearchParams();
  const back = params.get('fra') === 'stripe';
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const q = useQuery({ queryKey: ['billing'], queryFn: () => get<Billing>('/billing') });
  const [busy, setBusy] = useState(false);
  const b = q.data;
  const live = !!b?.status && LIVE.includes(b.status);
  const waiting = back && b && !live;

  // back from Checkout: the webhook may be a few seconds behind
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => q.refetch(), 2000);
    return () => clearInterval(t);
  }, [waiting, q]);
  useEffect(() => {
    if (back && live) qc.invalidateQueries({ queryKey: ['tenant'] });
  }, [back, live, qc]);

  async function go(path: string) {
    setBusy(true);
    try {
      const { url } = await post<{ url: string }>(path, {});
      window.location.assign(url);
    } catch (e) {
      toast(errorText(e));
      setBusy(false);
    }
  }

  return (
    <KontoBox title={T('Abonnement')} sub={tenant?.name}>
      {q.isLoading ? (
        <Skeleton />
      ) : (q.error as { status?: number } | null)?.status === 403 ? (
        // a refusal is not a failed fetch: say who the page is for, no Prøv igjen (#272 B-46)
        <Notice tone="info" title={T('Abonnementet er for administratorer')}>
          {T('Be en administrator i bedriften om å se eller endre abonnementet.')}
        </Notice>
      ) : q.isError || !b ? (
        <LoadError what={T('abonnementet')} onRetry={() => q.refetch()} />
      ) : (
        <div className="opt" data-testid="billing">
          <div className="auth-price">
            <b data-testid="billing-sum">
              {T(
                b.quote.seats === 1
                  ? '{seats} ansatt i dag: {sum} per måned'
                  : '{seats} ansatte i dag: {sum} per måned',
                {
                  seats: b.quote.seats,
                  sum: kr(b.quote.monthly_ex_vat),
                },
              )}
            </b>
            <span>{T('eks. mva ({mva} % kommer i tillegg).', { mva: b.quote.mva_percent })}</span>
          </div>
          <ul className="auth-list">
            <li>
              {T('{base} per måned inkludert {included} ansatte, {seat} per ansatt utover det.', {
                base: kr(b.quote.base_kr),
                included: b.quote.included,
                seat: kr(b.quote.seat_kr),
              })}
            </li>
            <li>
              {T(
                'Administratorer og deaktiverte personer telles ikke. Nye ansatte kommer med fra neste faktura.',
              )}
            </li>
            {b.status ? (
              <li data-testid="billing-status">
                {T(STATUS[b.status] ?? b.status)}
                {b.paid_until && live ? `, ${T('neste faktura {date}', { date: day(b.paid_until) })}` : ''}
              </li>
            ) : b.trial_ends_at ? (
              <li>
                {b.access === 'full'
                  ? T('Gratis til {date}. Legger du inn kort nå, belastes du først da.', {
                      date: day(b.trial_ends_at),
                    })
                  : b.access === 'read'
                    ? T('Over. Du kan se og eksportere til {date}, så låses bedriften.', {
                        date: day(b.grace_ends_at),
                      })
                    : T('Over, og bedriften er låst til abonnementet er startet.')}
              </li>
            ) : null}
          </ul>

          {waiting ? (
            <output className="auth-note">{T('Venter på bekreftelse fra Stripe …')}</output>
          ) : !b.enabled ? (
            <p className="auth-note">{T('Betaling er ikke slått på ennå. Ta kontakt med Timefly.')}</p>
          ) : (
            <>
              {!live ? (
                <Button tone="pri" disabled={busy} onClick={() => go('/billing/checkout')}>
                  {T('Legg inn kort og start abonnementet')}
                </Button>
              ) : null}
              {b.has_customer ? (
                <Button disabled={busy} onClick={() => go('/billing/portal')}>
                  {T('Administrer betaling')}
                </Button>
              ) : null}
            </>
          )}
          <p className="auth-note">
            {T('Kortet håndteres av Stripe. Du kan si opp når som helst, og det gjelder fra neste måned.')}
          </p>
        </div>
      )}
      <p className="auth-alt auth-links">
        <Link to="/" className="tap">
          {T('Tilbake til Timefly')}
        </Link>
      </p>
    </KontoBox>
  );
}
